// src/components/Navbar.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import logo from "../assets/Logo_exp.png";

/* Taglines match the ones on each service page, so the menu and the page
   it opens say the same thing. */
const serviceNavGroups = [
  {
    label: "Loans",
    items: [
      {
        id: "mortgages",
        label: "Mortgages",
        blurb: "Clear, independent home loan advice",
      },
      {
        id: "first-home-buyers",
        label: "First Home Buyers",
        blurb: "Buying your first place",
      },
      {
        id: "investment-property",
        label: "Investment Property",
        blurb: "Finance for property investors",
      },
      {
        id: "refinancing",
        label: "Refinancing",
        blurb: "Review your loan and reduce interest",
      },
      {
        id: "construction-loans",
        label: "Construction Loans",
        blurb: "Finance for builds and renovations",
      },
      {
        id: "commercial-property",
        label: "Commercial Property",
        blurb: "Lending for business premises",
      },
    ],
  },
  {
    label: "Protection",
    items: [
      {
        id: "life-insurance",
        label: "Life Insurance",
        blurb: "Cover for the people you leave behind",
      },
      {
        id: "income-protection",
        label: "Income Protection",
        blurb: "Part of your pay if you can’t work",
      },
      {
        id: "health-trauma",
        label: "Health & Trauma",
        blurb: "A lump sum if you’re seriously ill",
      },
      {
        id: "mortgage-protection",
        label: "Mortgage Protection",
        blurb: "Repayments covered if you can’t work",
      },
    ],
  },
  {
    label: "Business",
    items: [
      {
        id: "business-loans",
        label: "Business Loans",
        blurb: "Funding for growth and acquisitions",
      },
      {
        id: "business-finance",
        label: "Business Finance",
        blurb: "Funding to run and grow a business",
      },
      {
        id: "equipment-finance",
        label: "Equipment Finance",
        blurb: "Upgrade equipment without cash pressure",
      },
    ],
  },
];

/* Flat list (the mobile sheet reads it in order); `group` splits it into
   columns in the desktop panel, the way Services is split. */
const aboutNavItems = [
  {
    to: "/about",
    label: "About Us",
    group: "Company",
    blurb: "Who we are and how we work",
  },
  {
    to: "/about/team",
    label: "Our Team",
    group: "Company",
    blurb: "The advisers you will deal with",
  },
  {
    to: "/about/careers",
    label: "Careers",
    group: "Company",
    blurb: "Register your interest for future roles",
  },
  {
    to: "/about/privacy",
    label: "Privacy",
    group: "Legal",
    blurb: "How we handle your information",
  },
  {
    to: "/about/disclosure",
    label: "Disclosure",
    group: "Legal",
    blurb: "Licensing, fees and commission",
  },
];

const aboutNavGroups = ["Company", "Legal"].map((label) => ({
  label,
  items: aboutNavItems.filter((item) => item.group === label),
}));

/* Passing the cursor over a sibling nav item on the way down to an open panel
   should not swap or dismiss it. Switching waits a beat; closing waits longer,
   since the panel's far corners are a long diagonal from the trigger. */
const SWITCH_DELAY = 140;
const CLOSE_DELAY = 260;

function Navbar() {
  // Which desktop dropdown is open: "services", "about", or null.
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const headerRef = useRef(null);
  const hoverTimer = useRef(null);
  const canHover = useRef(false);
  const location = useLocation();

  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  const closeAll = useCallback(() => {
    clearHoverTimer();
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setMobileAboutOpen(false);
  }, [clearHoverTimer]);

  // The bar tightens and its shadow deepens once the page moves.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Clicking outside the header, or pressing Escape, closes whatever is open.
  useEffect(() => {
    if (!openMenu && !mobileOpen) return undefined;

    const onPointerDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target))
        closeAll();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAll();
    };

    const onFocusIn = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target))
        closeAll();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [openMenu, mobileOpen, closeAll]);

  // Stop the page behind the mobile sheet from scrolling.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  // Any navigation closes the menus, including clicks on the page already shown.
  useEffect(() => {
    closeAll();
  }, [location.pathname, closeAll]);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  /* On a touch screen the browser fires a synthetic mouseover just before the
     click. Without this the hover would open the menu and the tap would close
     it again, leaving Services unopenable on a tablet. */
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => {
      canHover.current = mq.matches;
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const toggleMenu = (name) => {
    clearHoverTimer();
    setOpenMenu((prev) => (prev === name ? null : name));
  };

  // Hovering a trigger opens it at once from rest. With another menu already
  // open it waits, so brushing past on the way to that menu does not swap it.
  const hoverTrigger = (name) => {
    if (!canHover.current) return;
    clearHoverTimer();
    if (openMenu === null || openMenu === name) {
      setOpenMenu(name);
      return;
    }
    hoverTimer.current = setTimeout(() => setOpenMenu(name), SWITCH_DELAY);
  };

  // Hovering a plain link, or leaving the header, closes on a delay that the
  // next hoverTrigger cancels.
  const hoverAway = () => {
    if (!canHover.current) return;
    clearHoverTimer();
    if (!openMenu) return;
    hoverTimer.current = setTimeout(() => setOpenMenu(null), CLOSE_DELAY);
  };

  return (
    <header
      ref={headerRef}
      className={`site-header ${scrolled ? "is-scrolled" : ""} ${
        openMenu ? "is-menu-open" : ""
      }`}
      onMouseLeave={hoverAway}
    >
      <div className="navbar">
        <Link to="/" className="nav-logo" onClick={closeAll}>
          <img src={logo} alt="Expert Finance" />
        </Link>

        <nav className="nav-center" aria-label="Main navigation">
          <ul className="nav-links">
            <li className="nav-item" onMouseEnter={hoverAway}>
              <NavLink to="/" end className="nav-link" onClick={closeAll}>
                Home
              </NavLink>
            </li>

            <li
              className={`nav-item has-dropdown services-nav ${
                openMenu === "services" ? "is-open" : ""
              }`}
              onMouseEnter={() => hoverTrigger("services")}
            >
              <button
                type="button"
                className="nav-link nav-link-dropdown"
                onClick={() => toggleMenu("services")}
                aria-expanded={openMenu === "services"}
                aria-haspopup="true"
                aria-controls="services-mega-menu"
              >
                Services
                <svg
                  className="nav-caret"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m3 4.5 3 3 3-3" />
                </svg>
              </button>

              <div
                id="services-mega-menu"
                className={`mega-menu ${openMenu === "services" ? "is-open" : ""}`}
              >
                <div className="mega-menu-inner">
                  {serviceNavGroups.map((group) => (
                    <div className="mega-column" key={group.label}>
                      <div className="mega-heading">{group.label}</div>
                      {group.items.map((item) => (
                        <Link
                          key={item.id}
                          to={`/services/${item.id}`}
                          className="mega-link"
                          onClick={closeAll}
                        >
                          <span className="mega-link-text">
                            <span className="mega-link-label">
                              {item.label}
                            </span>
                            <span className="mega-link-blurb">
                              {item.blurb}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  ))}

                  <div className="mega-feature">
                    <div className="mega-feature-eyebrow">
                      Not sure where to start?
                    </div>
                    <p className="mega-feature-copy">
                      Give us a quick outline of where you’re at and we’ll point
                      you in the right direction. No cost, no obligation.
                    </p>
                    <Link
                      to="/contact"
                      className="mega-feature-cta"
                      onClick={closeAll}
                    >
                      Talk to an adviser
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" />
                      </svg>
                    </Link>
                    <Link
                      to="/calculator"
                      className="mega-feature-link"
                      onClick={closeAll}
                    >
                      Or try the repayment calculator
                    </Link>
                  </div>
                </div>
              </div>
            </li>

            <li
              className={`nav-item has-dropdown about-nav ${
                openMenu === "about" ? "is-open" : ""
              }`}
              onMouseEnter={() => hoverTrigger("about")}
            >
              <button
                type="button"
                className="nav-link nav-link-dropdown"
                onClick={() => toggleMenu("about")}
                aria-expanded={openMenu === "about"}
                aria-haspopup="true"
                aria-controls="about-mega-menu"
              >
                About Us
                <svg
                  className="nav-caret"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m3 4.5 3 3 3-3" />
                </svg>
              </button>

              <div
                id="about-mega-menu"
                className={`mega-menu about-mega-menu ${
                  openMenu === "about" ? "is-open" : ""
                }`}
              >
                <div className="mega-menu-inner about-mega-inner">
                  {aboutNavGroups.map((group) => (
                    <div className="mega-column" key={group.label}>
                      <div className="mega-heading">{group.label}</div>
                      {group.items.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.to === "/about"}
                          className="mega-link"
                          onClick={closeAll}
                        >
                          <span className="mega-link-text">
                            <span className="mega-link-label">
                              {item.label}
                            </span>
                            <span className="mega-link-blurb">
                              {item.blurb}
                            </span>
                          </span>
                        </NavLink>
                      ))}
                    </div>
                  ))}

                  <div className="mega-feature">
                    <div className="mega-feature-eyebrow">
                      Meet the advisers
                    </div>
                    <p className="mega-feature-copy">
                      You deal with an adviser directly, not a call centre.
                    </p>
                    <Link
                      to="/about/team"
                      className="mega-feature-cta"
                      onClick={closeAll}
                    >
                      Meet the team
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" />
                      </svg>
                    </Link>
                    <Link
                      to="/contact"
                      className="mega-feature-link"
                      onClick={closeAll}
                    >
                      Or book a free chat
                    </Link>
                  </div>
                </div>
              </div>
            </li>

            <li className="nav-item" onMouseEnter={hoverAway}>
              <NavLink to="/calculator" className="nav-link" onClick={closeAll}>
                Calculator
              </NavLink>
            </li>
            <li className="nav-item" onMouseEnter={hoverAway}>
              <NavLink to="/contact" className="nav-link" onClick={closeAll}>
                Contact
              </NavLink>
            </li>
          </ul>
        </nav>

        <div className="nav-right">
          <Link to="/contact" className="nav-cta" onClick={closeAll}>
            Book a free chat
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" />
            </svg>
          </Link>

          <button
            className={`nav-toggle ${mobileOpen ? "is-open" : ""}`}
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      <div className={`mobile-menu ${mobileOpen ? "is-open" : ""}`}>
        <nav className="mobile-menu-inner" aria-label="Mobile navigation">
          <ul className="mobile-menu-list">
            <li>
              <NavLink to="/" end className="mobile-link" onClick={closeAll}>
                Home
              </NavLink>
            </li>

            <li className="mobile-group">
              <button
                type="button"
                className="mobile-group-toggle"
                onClick={() => setMobileServicesOpen((prev) => !prev)}
                aria-expanded={mobileServicesOpen}
              >
                <span>Services</span>
                <svg
                  className={`mobile-caret ${mobileServicesOpen ? "is-open" : ""}`}
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m3 4.5 3 3 3-3" />
                </svg>
              </button>

              {mobileServicesOpen && (
                <div className="mobile-panel">
                  {serviceNavGroups.map((group) => (
                    <div className="mobile-panel-group" key={group.label}>
                      <div className="mobile-panel-title">{group.label}</div>
                      {group.items.map((item) => (
                        <NavLink
                          key={item.id}
                          to={`/services/${item.id}`}
                          className="mobile-sub-link"
                          onClick={closeAll}
                        >
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </li>

            <li className="mobile-group">
              <button
                type="button"
                className="mobile-group-toggle"
                onClick={() => setMobileAboutOpen((prev) => !prev)}
                aria-expanded={mobileAboutOpen}
              >
                <span>About Us</span>
                <svg
                  className={`mobile-caret ${mobileAboutOpen ? "is-open" : ""}`}
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m3 4.5 3 3 3-3" />
                </svg>
              </button>

              {mobileAboutOpen && (
                <div className="mobile-panel">
                  <div className="mobile-panel-group">
                    {aboutNavItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/about"}
                        className="mobile-sub-link"
                        onClick={closeAll}
                      >
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </li>

            <li>
              <NavLink
                to="/calculator"
                className="mobile-link"
                onClick={closeAll}
              >
                Calculator
              </NavLink>
            </li>
            <li>
              <NavLink to="/contact" className="mobile-link" onClick={closeAll}>
                Contact
              </NavLink>
            </li>
          </ul>

          <div className="mobile-menu-footer">
            <Link to="/contact" className="mobile-cta" onClick={closeAll}>
              Book a free chat
            </Link>
            <a href="tel:+6492176765" className="mobile-call">
              Or call 09 217 6765
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Navbar;
